/**
 * ImageViewer utility functions
 * Helper functions for the ImageViewer components
 */

/**
 * Converts bytes to megabytes with 1 decimal place
 * @param bytes - The number of bytes to convert
 * @returns Formatted string in MB (e.g., "0.5", "1.2", "10.0")
 * @throws Error if bytes is not a valid number
 */
export const formatBytesToMB = (bytes: number): string => {
  if (typeof bytes !== 'number' || isNaN(bytes)) {
    throw new Error('Invalid input: bytes must be a number')
  }
  const mb = bytes / (1024 * 1024)
  return mb.toFixed(1) // returns a string like "0.5"
}

/**
 * Converts bytes to a human-readable format (B, KB, MB, GB)
 * @param bytes - The number of bytes to convert
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "1.5 MB", "2.3 GB")
 */
export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/**
 * Validates if a file is an image based on its MIME type
 * @param mimeType - The MIME type of the file
 * @returns True if the file is an image
 */
export const isImageFile = (mimeType: string): boolean => {
  return mimeType.startsWith('image/')
}

/**
 * Validates if a file is a DICOM file based on its extension or MIME type
 * @param fileName - The name of the file
 * @param mimeType - The MIME type of the file (optional)
 * @returns True if the file is a DICOM file
 */
export const isDicomFile = (fileName: string, mimeType?: string): boolean => {
  const dicomExtensions = ['.dcm', '.dicom']
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'))

  return dicomExtensions.includes(extension) || mimeType === 'application/dicom'
}

/**
 * Gets the file extension from a filename
 * @param fileName - The name of the file
 * @returns The file extension (including the dot)
 */
export const getFileExtension = (fileName: string): string => {
  return fileName.toLowerCase().substring(fileName.lastIndexOf('.'))
}

/**
 * Formats a date for display in ImageViewer components
 * @param date - The date to format
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
